import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Package, Search, CheckSquare, Boxes, Key } from 'lucide-react';

// Single tool assignment dialog
export const AssignToolDialog = ({ open, onOpenChange, tools, onSubmit }) => {
  const [form, setForm] = useState({
    tool_id: '', quantity: 1, duration_type: 'permanent', end_date: '',
    assignment_type: 'new', replacement_reason: '', old_tool_id: '',
  });

  const reset = () => setForm({
    tool_id: '', quantity: 1, duration_type: 'permanent', end_date: '',
    assignment_type: 'new', replacement_reason: '', old_tool_id: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
    reset();
  };

  const handleToolChange = (value) => {
    const tool = tools.find(t => t.id === value);
    if (tool?.expiration_date) {
      setForm({ ...form, tool_id: value, duration_type: 'temporary', end_date: tool.expiration_date.split('T')[0] });
    } else {
      setForm({ ...form, tool_id: value });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Nouvelle Affectation</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Outil *</Label>
            <Select value={form.tool_id} onValueChange={handleToolChange} required>
              <SelectTrigger data-testid="assign-tool-select" className="rounded-lg"><SelectValue placeholder="Sélectionner un outil" /></SelectTrigger>
              <SelectContent>
                {tools.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <div className="flex flex-col">
                      <span>{t.name} - {t.type}</span>
                      <span className="text-xs text-muted-foreground">N° {t.article_number} - {t.quantity_available} dispo.{t.expiration_date && ` - Exp: ${new Date(t.expiration_date).toLocaleDateString('fr-FR')}`}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Quantité *</Label>
            <Input data-testid="assign-quantity-input" type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} required className="rounded-lg" />
          </div>
          <div className="space-y-2">
            <Label>Type d'affectation *</Label>
            <Select value={form.assignment_type} onValueChange={(v) => setForm({ ...form, assignment_type: v })} required>
              <SelectTrigger data-testid="assign-type-select" className="rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">Nouvel outil</SelectItem>
                <SelectItem value="replacement">Remplacement (Perte/Usure)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.assignment_type === 'replacement' && (
            <>
              <div className="space-y-2">
                <Label>Ancien outil *</Label>
                <Select value={form.old_tool_id} onValueChange={(v) => setForm({ ...form, old_tool_id: v })} required>
                  <SelectTrigger data-testid="assign-old-tool-select" className="rounded-lg"><SelectValue placeholder="Sélectionner l'ancien outil" /></SelectTrigger>
                  <SelectContent>
                    {tools.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} - {t.type}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Raison du remplacement *</Label>
                <Select value={form.replacement_reason} onValueChange={(v) => setForm({ ...form, replacement_reason: v })} required>
                  <SelectTrigger data-testid="assign-reason-select" className="rounded-lg"><SelectValue placeholder="Sélectionner la raison" /></SelectTrigger>
                  <SelectContent>
                    {['Perte', 'Casse', 'Usure', 'Endommagé', 'Obsolète'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div className="space-y-2">
            <Label>Durée</Label>
            <Select value={form.duration_type} onValueChange={(v) => setForm({ ...form, duration_type: v, end_date: '' })}>
              <SelectTrigger data-testid="assign-duration-type-select" className="rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="permanent">Permanent</SelectItem>
                <SelectItem value="temporary">Temporaire (avec date de retour)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.duration_type === 'temporary' && (
            <div className="space-y-2">
              <Label>Date de retour prévue *</Label>
              <Input data-testid="assign-end-date-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required className="rounded-lg" />
              {form.tool_id && tools.find(t => t.id === form.tool_id)?.expiration_date && (
                <p className="text-xs text-blue-600 flex items-center gap-1"><Calendar className="w-3 h-3" /> Date appliquée automatiquement selon l'expiration de l'outil</p>
              )}
            </div>
          )}
          <Button data-testid="assign-submit-button" type="submit" className="w-full rounded-full" disabled={form.duration_type === 'temporary' && !form.end_date}>Affecter l'outil</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Replace tool dialog
export const ReplaceToolDialog = ({ open, onOpenChange, tools, selectedAssignment, onSubmit }) => {
  const [form, setForm] = useState({ new_tool_id: '', quantity: 1, reason: '', duration_type: 'permanent', end_date: '' });
  const reset = () => setForm({ new_tool_id: '', quantity: 1, reason: '', duration_type: 'permanent', end_date: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form, selectedAssignment);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Remplacer un Outil</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {selectedAssignment && (
            <div className="p-4 bg-muted rounded-lg space-y-2">
              <p className="text-sm font-medium">Outil actuel:</p>
              <p className="text-lg font-heading">{selectedAssignment.tool?.name}</p>
              <p className="text-sm text-muted-foreground">{selectedAssignment.tool?.type}</p>
              <p className="text-xs font-mono text-muted-foreground">N° {selectedAssignment.tool?.article_number}</p>
            </div>
          )}
          <div className="space-y-2">
            <Label>Raison *</Label>
            <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })} required>
              <SelectTrigger data-testid="replace-reason-select" className="rounded-lg"><SelectValue placeholder="Sélectionner une raison" /></SelectTrigger>
              <SelectContent>
                {['Perte', 'Casse', 'Endommagé', 'Obsolète', 'Autre'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Nouvel outil *</Label>
            <Select value={form.new_tool_id} onValueChange={(v) => setForm({ ...form, new_tool_id: v })} required>
              <SelectTrigger data-testid="replace-tool-select" className="rounded-lg"><SelectValue placeholder="Sélectionner un outil disponible" /></SelectTrigger>
              <SelectContent>
                {tools.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <div className="flex flex-col">
                      <span>{t.name} - {t.type}</span>
                      <span className="text-xs text-muted-foreground">N° {t.article_number} - {t.quantity_available} dispo.</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Quantité *</Label>
            <Input data-testid="replace-quantity-input" type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} required className="rounded-lg" />
          </div>
          <div className="space-y-2">
            <Label>Durée</Label>
            <Select value={form.duration_type} onValueChange={(v) => setForm({ ...form, duration_type: v, end_date: '' })}>
              <SelectTrigger data-testid="replace-duration-type-select" className="rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="permanent">Permanent</SelectItem>
                <SelectItem value="temporary">Temporaire</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.duration_type === 'temporary' && (
            <div className="space-y-2">
              <Label>Date de retour *</Label>
              <Input data-testid="replace-end-date-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required className="rounded-lg" />
            </div>
          )}
          <Button data-testid="replace-submit-button" type="submit" className="w-full rounded-full" disabled={form.duration_type === 'temporary' && !form.end_date}>Confirmer le remplacement</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Assign rental equipment dialog
export const AssignRentalDialog = ({ open, onOpenChange, collaboratorName, rentalEquipment, onSubmit }) => {
  const [form, setForm] = useState({ equipment_id: '', quantity: 1, expected_return_date: '' });
  const reset = () => setForm({ equipment_id: '', quantity: 1, expected_return_date: '' });

  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); reset(); };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Assigner un Équipement de Location</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200">
            <p className="text-sm font-medium text-blue-700 dark:text-blue-400">Collaborateur:</p>
            <p className="text-lg font-heading">{collaboratorName}</p>
          </div>
          <div className="space-y-2">
            <Label>Équipement de location *</Label>
            <Select value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v })} required>
              <SelectTrigger data-testid="assign-rental-equipment-select" className="rounded-lg"><SelectValue placeholder="Sélectionner un équipement" /></SelectTrigger>
              <SelectContent>
                {rentalEquipment.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name} ({e.quantity_available} disponible{e.quantity_available > 1 ? 's' : ''})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {rentalEquipment.length === 0 && <p className="text-xs text-muted-foreground">Aucun équipement disponible</p>}
          </div>
          <div className="space-y-2">
            <Label>Quantité *</Label>
            <Input data-testid="assign-rental-quantity-input" type="number" min="1"
              max={rentalEquipment.find(e => e.id === form.equipment_id)?.quantity_available || 1}
              value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} required className="rounded-lg" />
          </div>
          <div className="space-y-2">
            <Label>Date de retour prévue</Label>
            <Input data-testid="assign-rental-return-date-input" type="date" value={form.expected_return_date} onChange={(e) => setForm({ ...form, expected_return_date: e.target.value })} className="rounded-lg" />
          </div>
          <Button data-testid="assign-rental-submit-button" type="submit" className="w-full rounded-full" disabled={!form.equipment_id || rentalEquipment.length === 0}>
            <Key className="w-4 h-4 mr-2" /> Assigner l'équipement
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Multi-assign dialog
export const MultiAssignDialog = ({ open, onOpenChange, tools, onSubmit }) => {
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState('');
  const [durationType, setDurationType] = useState('permanent');
  const [endDate, setEndDate] = useState('');

  const reset = () => { setSelected({}); setSearch(''); setDurationType('permanent'); setEndDate(''); };

  const filtered = tools.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.type?.toLowerCase().includes(search.toLowerCase()) ||
    t.article_number?.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (tool) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[tool.id]) { delete next[tool.id]; } else { next[tool.id] = { quantity: 1, tool }; }
      return next;
    });
  };

  const updateQty = (id, qty) => {
    setSelected(prev => ({ ...prev, [id]: { ...prev[id], quantity: Math.max(1, Math.min(qty, prev[id].tool.quantity_available)) } }));
  };

  const count = Object.keys(selected).length;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(selected, durationType, endDate);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CheckSquare className="w-5 h-5" /> Attribution Multiple
            {count > 0 && <Badge variant="default" className="ml-2">{count} sélectionné(s)</Badge>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input data-testid="multi-assign-search" placeholder="Rechercher un outil..." className="pl-10 rounded-lg" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="flex-1 overflow-y-auto border rounded-lg">
            {filtered.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground"><Package className="w-12 h-12 mx-auto mb-2 opacity-50" /><p>Aucun outil disponible</p></div>
            ) : (
              <div className="divide-y">
                {filtered.map((tool) => (
                  <div key={tool.id} className={`p-3 flex items-center gap-3 hover:bg-muted/50 cursor-pointer transition-colors ${selected[tool.id] ? 'bg-primary/5 border-l-4 border-l-primary' : ''}`} onClick={() => toggle(tool)}>
                    <div className={`w-5 h-5 shrink-0 rounded border-2 flex items-center justify-center transition-colors ${selected[tool.id] ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                      {selected[tool.id] && <CheckSquare className="w-3 h-3 text-primary-foreground" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{tool.name}</span>
                        <Badge variant="outline" className="text-xs shrink-0">{tool.quantity_available} dispo.</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <span>{tool.type}</span><span>N° {tool.article_number}</span>
                        {tool.expiration_date && <span className="text-orange-500">Exp: {new Date(tool.expiration_date).toLocaleDateString('fr-FR')}</span>}
                      </div>
                    </div>
                    {selected[tool.id] && (
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <Label className="text-xs">Qté:</Label>
                        <Input data-testid={`multi-qty-${tool.id}`} type="number" min="1" max={tool.quantity_available} value={selected[tool.id].quantity} onChange={(e) => updateQty(tool.id, parseInt(e.target.value) || 1)} className="w-16 h-8 text-center rounded" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Durée</Label>
            <Select value={durationType} onValueChange={(v) => { setDurationType(v); if (v === 'permanent') setEndDate(''); }}>
              <SelectTrigger data-testid="multi-assign-duration-type" className="rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="permanent">Permanent</SelectItem>
                <SelectItem value="temporary">Temporaire</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {durationType === 'temporary' && (
            <div className="space-y-2">
              <Label>Date de retour *</Label>
              <Input data-testid="multi-assign-end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required className="rounded-lg" />
            </div>
          )}
          <Button data-testid="multi-assign-submit" onClick={handleSubmit} className="w-full rounded-full" disabled={count === 0 || (durationType === 'temporary' && !endDate)}>
            <CheckSquare className="w-4 h-4 mr-2" /> Affecter {count} outil{count > 1 ? 's' : ''}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Kit assign dialog
export const KitAssignDialog = ({ open, onOpenChange, collaboratorName, kits, onSubmit }) => {
  const [kitId, setKitId] = useState('');
  const [durationType, setDurationType] = useState('permanent');
  const [endDate, setEndDate] = useState('');

  const reset = () => { setKitId(''); setDurationType('permanent'); setEndDate(''); };
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(kitId, durationType, endDate); reset(); };
  const selectedKit = kits.find(k => k.id === kitId);

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Boxes className="w-5 h-5" /> Affecter un Kit</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Collaborateur:</p>
            <p className="font-medium font-heading">{collaboratorName}</p>
          </div>
          <div className="space-y-2">
            <Label>Kit *</Label>
            <Select value={kitId} onValueChange={setKitId} required>
              <SelectTrigger data-testid="kit-select" className="rounded-lg"><SelectValue placeholder="Sélectionner un kit" /></SelectTrigger>
              <SelectContent>
                {kits.map((k) => (
                  <SelectItem key={k.id} value={k.id}>
                    <div className="flex flex-col">
                      <span className="font-medium">{k.name}</span>
                      <span className="text-xs text-muted-foreground">{k.items.length} outil{k.items.length > 1 ? 's' : ''}{k.description && ` - ${k.description}`}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {kits.length === 0 && <p className="text-xs text-muted-foreground">Aucun kit disponible. <a href="/kits" className="text-primary underline">Créer un kit</a></p>}
          </div>
          {selectedKit && (
            <div className="p-3 bg-muted/50 rounded-lg space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Contenu du kit</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {selectedKit.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span>{item.tool_name}</span>
                    <Badge variant="outline" className="text-xs">x{item.quantity}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label>Durée</Label>
            <Select value={durationType} onValueChange={(v) => { setDurationType(v); if (v === 'permanent') setEndDate(''); }}>
              <SelectTrigger data-testid="kit-duration-type" className="rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="permanent">Permanent</SelectItem>
                <SelectItem value="temporary">Temporaire</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {durationType === 'temporary' && (
            <div className="space-y-2">
              <Label>Date de retour *</Label>
              <Input data-testid="kit-end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required className="rounded-lg" />
            </div>
          )}
          <Button data-testid="kit-assign-submit" type="submit" className="w-full rounded-full" disabled={!kitId || (durationType === 'temporary' && !endDate)}>
            <Boxes className="w-4 h-4 mr-2" /> Affecter le Kit
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
